import { useEffect, useState } from "react";
import { Trophy, Calendar, Gift, Crown } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";

export default function PromoContests() {
  const [data, setData] = useState({ active: [], past: [], standings: {} });
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/aff-panel/contests");
      setData(r.data || { active: [], past: [], standings: {} });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <PageHeader
        eyebrow="COMPETITION"
        title="Affiliate Contests"
        subtitle="Win cash prizes by climbing the affiliate ladder during contest windows."
      />
      <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-emerald-200/80 mb-3 px-1">🏆 Active Contests</div>
      {loading ? (
        <div className="p-glass p-6 text-center text-white/55">Loading…</div>
      ) : data.active.length === 0 ? (
        <div className="p-glass p-10 text-center">
          <Trophy size={28} className="mx-auto text-emerald-200 mb-2" />
          <div className="text-white font-bold">No active contests right now</div>
          <div className="text-white/55 text-sm">Stay tuned — new contests are added regularly.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {data.active.map((c) => (
            <ContestCard key={c.id} contest={c} standings={data.standings?.[c.id] || []} />
          ))}
        </div>
      )}

      {data.past.length > 0 && (
        <>
          <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-white/45 mt-8 mb-3 px-1">Past Contests</div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {data.past.map((c) => (
              <div key={c.id} className="p-glass" style={{ padding: 16, opacity: 0.85 }}>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold">Ended {(c.end_at || "").slice(0, 10)}</div>
                <div className="font-black text-white mt-1">{c.name}</div>
                {(c.winners || []).length > 0 && (
                  <div className="mt-2 text-[12px] text-white/70">Winners: {(c.winners || []).map((w) => `#${w.rank} ${w.username}`).join(", ")}</div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ContestCard({ contest, standings }) {
  const prizes = contest.prizes || [];
  return (
    <div className="p-glass p-card-hover" style={{ padding: 20, border: "1px solid rgba(34, 192, 115, 0.45)" }}>
      <div className="flex items-start gap-3 mb-3">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "rgba(34, 192, 115, 0.15)", border: "1px solid rgba(34, 192, 115, 0.45)" }}>
          <Trophy size={22} className="text-emerald-200" />
        </div>
        <div className="min-w-0">
          <div className="font-black text-white text-lg leading-tight truncate">{contest.name}</div>
          <div className="text-white/55 text-[12.5px]">{contest.description || "\u00a0"}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <Info icon={Gift} label="Prize Pool" value={`৳${(contest.prize_pool || 0).toLocaleString()}`} />
        <Info icon={Calendar} label="Window" value={`${(contest.start_at || "").slice(0, 10)} → ${(contest.end_at || "").slice(0, 10)}`} />
      </div>
      {prizes.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {prizes.map((p) => (
            <span key={p.rank} className="px-2 py-0.5 rounded-md text-[11px] font-bold" style={{ background: "rgba(34, 192, 115, 0.12)", border: "1px solid rgba(34, 192, 115, 0.35)", color: "#a7f3d0" }}>
              #{p.rank} → ৳{Number(p.prize || 0).toLocaleString()}
            </span>
          ))}
        </div>
      )}
      {standings.length > 0 && (
        <div className="rounded-xl p-3 text-[12px]" style={{ background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-200/80 mb-2">Live Standings (top 5)</div>
          {standings.slice(0, 5).map((s, i) => (
            <div key={s.account_id} className="flex items-center justify-between py-1">
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-bold text-white/65 w-5 text-right">#{i + 1}</span>
                <span className="font-bold text-white truncate">{s.username}</span>
              </div>
              <span className="font-bold text-emerald-100">৳{(s.value || 0).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="p-2 rounded-lg flex items-center gap-2" style={{ background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.06)" }}>
      <Icon size={14} className="text-emerald-200" />
      <div>
        <div className="text-[9.5px] uppercase tracking-[0.22em] text-white/45 font-bold">{label}</div>
        <div className="text-white font-bold text-[12.5px]">{value}</div>
      </div>
    </div>
  );
}
